/**
 * Molecule: ReferencePopover
 *
 * Click-to-open popover anchored to a trigger button. Shows a grid of
 * navigation cards (Contacts, Handbook, Cost Catalog, Consumables, Files)
 * for surfaces that no longer live in the main sidebar. Closes on outside
 * click, route change, or Escape.
 */
import React, { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { tokens } from '@/design-system/tokens'
import { BookOpen, Users, Package, Layers, FolderOpen, Wrench, MapPin } from 'lucide-react'

const CARDS = [
  { to: '/contacts',       label: 'Contacts',     Icon: Users },
  { to: '/handbook',       label: 'Handbook',     Icon: BookOpen },
  { to: '/cost-catalogue', label: 'Cost Catalog', Icon: Package },
  { to: '/consumables',    label: 'Consumables',  Icon: Layers },
  { to: '/equipment',      label: 'Equipment',    Icon: Wrench },
  { to: '/locations',      label: 'Locations',    Icon: MapPin },
  { to: '/files',          label: 'Files',        Icon: FolderOpen },
] as const

interface ReferencePopoverProps {
  triggerStyle?: React.CSSProperties
  brandColor?: string
}

export const ReferencePopover: React.FC<ReferencePopoverProps> = ({ triggerStyle, brandColor }) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  useEffect(() => { setOpen(false) }, [location.pathname])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const accent = brandColor || tokens.color.brand.gold

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Reference"
        aria-label="Open reference library"
        style={{
          background: open ? `${accent}26` : 'none',
          border: 'none',
          color: open ? accent : tokens.color.text.muted,
          cursor: 'pointer',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '6px',
          transition: 'background 0.15s, color 0.15s',
          ...triggerStyle,
        }}
      >
        <BookOpen size={16} />
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          right: 0,
          top: 34,
          width: 320,
          padding: 12,
          background: 'var(--bg-surface)',
          border: `1px solid ${tokens.color.border.default}`,
          borderRadius: 10,
          boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
          zIndex: 200,
          fontFamily: tokens.typography.fontFamily.sans,
        }}>
          <div style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: tokens.color.text.muted,
            margin: '2px 4px 8px',
          }}>
            Reference
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 8,
          }}>
            {CARDS.map(({ to, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                style={({ isActive }) => ({
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '14px 8px',
                  background: isActive ? `${accent}18` : tokens.color.background.elevated,
                  border: `1px solid ${isActive ? accent : tokens.color.border.default}`,
                  borderRadius: 8,
                  textDecoration: 'none',
                  color: isActive ? accent : tokens.color.text.primary,
                  fontSize: 12,
                  fontWeight: 500,
                  transition: 'background 0.1s, border-color 0.1s',
                })}
              >
                <Icon size={20} />
                <span>{label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
